import { DatePicker, Select, Card, Row, Col, Typography, Button, Space } from "antd";
import dayjs from "dayjs";

import { showErrorNotification } from "../../../../utils/notification";

const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * Platform minimum allowed start date for sales data query.
 */
const MIN_ALLOWED_DATE_STR = "2025-04-19";

/**
 * Declarative configurations for date presets.
 * Allows quick selection of common date ranges.
 */
const DATE_PRESETS = [
  {
    key: "all",
    label: "All Time",
    getStart: (_, minAllowed) => minAllowed,
    getEnd: (now) => now.endOf("day")
  },
  {
    key: "week",
    label: "This Week",
    getStart: (now) => now.startOf("week").startOf("day"),
    getEnd: (now) => now.endOf("day")
  },
  {
    key: "month",
    label: "This Month",
    getStart: (now) => now.startOf("month").startOf("day"),
    getEnd: (now) => now.endOf("day")
  },
  {
    key: "year",
    label: "This Year",
    getStart: (now) => now.startOf("year").startOf("day"),
    getEnd: (now) => now.endOf("day")
  }
];

/**
 * SalesFilters component provides controls for filtering sales analytics data.
 * Includes quick-access presets, a custom date range picker, and selective dropdowns for course, coupon, and analytics type filtering.
 *
 * @param {Object} props
 * @param {Date|null} props.startDate - The currently selected start date
 * @param {Date|null} props.endDate - The currently selected end date
 * @param {Array<{id: string, name: string}>} props.availableCourses - List of courses available for selection
 * @param {Array<{id: string, name: string}>} props.availableCoupons - List of coupon codes available for selection
 * @param {Array<{id: string, name: string}>} props.availableAnalyticsTypes - List of analytics types available for selection
 * @param {Function} props.onDateChange - Callback triggered when the selected date range changes
 * @param {Function} props.onCoursesChange - Callback triggered when selected courses change
 * @param {Function} props.onCouponsChange - Callback triggered when selected coupons change
 * @param {Function} props.onAnalyticsTypeChange - Callback triggered when selected analytics types change
 */
const SalesFilters = ({
  startDate,
  endDate,
  availableCourses = [],
  availableCoupons = [],
  availableAnalyticsTypes = [],
  onDateChange,
  onCoursesChange,
  onCouponsChange,
  onAnalyticsTypeChange,
  showRefunded,
  onShowRefundedChange
}) => {
  /**
   * Handles manual selection of a date range from the RangePicker.
   * Ensures selected start date is not before the minimum allowed platform start date.
   *
   * @param {Array<dayjs.Dayjs>|null} dates - Selected dayjs date range
   */
  const handleDateRangeChange = (dates) => {
    if (!dates) {
      onDateChange(null, null);
      return;
    }

    const minAllowed = dayjs(MIN_ALLOWED_DATE_STR).startOf("day");
    const [selectedStart, selectedEnd] = dates;

    if (selectedStart.isBefore(minAllowed)) {
      showErrorNotification("Start date cannot be before 19th April 2025.");
      return;
    }

    const normalizedStart = selectedStart.clone().startOf("day");
    const normalizedEnd = selectedEnd.clone().endOf("day");

    onDateChange(normalizedStart.toDate(), normalizedEnd.toDate());
  };

  /**
   * Sets the active date range to match a predefined preset configuration.
   *
   * @param {string} presetKey - Unique key identifying the selected preset
   */
  const handlePresetSelect = (presetKey) => {
    const preset = DATE_PRESETS.find((item) => item.key === presetKey);
    if (!preset) {
      return;
    }

    const now = dayjs();
    const minAllowed = dayjs(MIN_ALLOWED_DATE_STR).startOf("day");

    let computedStart = preset.getStart(now, minAllowed);
    const computedEnd = preset.getEnd(now);

    // Enforce minimum allowed date restriction
    if (computedStart.isBefore(minAllowed)) {
      computedStart = minAllowed;
    }

    onDateChange(computedStart.toDate(), computedEnd.toDate());
  };

  /**
   * Identifies which preset, if any, is currently selected.
   * Compares dates at the day level to avoid time mismatch issues.
   *
   * @returns {string|null} The key of the active preset, or null if custom range
   */
  const getActivePresetKey = () => {
    if (!startDate || !endDate) {
      return null;
    }

    const currentStart = dayjs(startDate);
    const currentEnd = dayjs(endDate);
    const now = dayjs();
    const minAllowed = dayjs(MIN_ALLOWED_DATE_STR).startOf("day");

    const matchingPreset = DATE_PRESETS.find((preset) => {
      const computedStart = preset.getStart(now, minAllowed);
      const computedEnd = preset.getEnd(now);

      return currentStart.isSame(computedStart, "day") && currentEnd.isSame(computedEnd, "day");
    });

    return matchingPreset ? matchingPreset.key : null;
  };

  const activePresetKey = getActivePresetKey();

  return (
    <Card title="Filters" style={{ marginBottom: "20px" }}>
      {/* Quick Select Presets Section */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap"
        }}
      >
        <Text strong>Quick Date Select:</Text>
        <Space size="small" wrap>
          {DATE_PRESETS.map((preset) => (
            <Button
              key={preset.key}
              size="small"
              type={activePresetKey === preset.key ? "primary" : "default"}
              onClick={() => handlePresetSelect(preset.key)}
            >
              {preset.label}
            </Button>
          ))}
        </Space>
      </div>

      {/* Filter Fields Grid */}
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={24} md={12} lg={8}>
          <Text strong>Date Range:</Text>
          <RangePicker
            style={{ width: "100%", marginTop: "8px" }}
            value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : []}
            disabledDate={(current) =>
              current && current < dayjs(MIN_ALLOWED_DATE_STR).startOf("day")
            }
            onChange={handleDateRangeChange}
          />
        </Col>

        <Col xs={24} sm={24} md={12} lg={8}>
          <Text strong>Analytics Types:</Text>
          <Select
            mode="multiple"
            style={{ width: "100%", marginTop: "8px" }}
            placeholder="Select Analytics Types"
            onChange={onAnalyticsTypeChange}
            allowClear
          >
            {availableAnalyticsTypes?.map((analyticsType) => (
              <Option key={analyticsType.id} value={analyticsType.id}>
                {analyticsType.name}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={24} md={12} lg={8}>
          <Text strong>Courses:</Text>
          <Select
            mode="multiple"
            style={{ width: "100%", marginTop: "8px" }}
            placeholder="Select Courses"
            onChange={onCoursesChange}
            allowClear
          >
            {availableCourses.map((course) => (
              <Option key={course.id} value={course.id}>
                {course.name}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={24} md={12} lg={8}>
          <Text strong>Coupon Codes:</Text>
          <Select
            mode="multiple"
            style={{ width: "100%", marginTop: "8px" }}
            placeholder="Select Coupon Codes"
            onChange={onCouponsChange}
            allowClear
          >
            {availableCoupons.map((coupon) => (
              <Option key={coupon.id} value={coupon.id}>
                {coupon.name}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={24} md={12} lg={8}>
          <Text strong>Payment Status:</Text>
          <Select
            style={{ width: "100%", marginTop: "8px" }}
            value={showRefunded}
            onChange={onShowRefundedChange}
          >
            <Option value={false}>Completed</Option>
            <Option value={true}>Refunded</Option>
          </Select>
        </Col>
      </Row>
    </Card>
  );
};

export default SalesFilters;
